import React, { useState, useRef } from 'react'
import { ExternalLink, Github, ChevronLeft, ChevronRight, Bot, Wallet, Building2, Car, Play, X } from 'lucide-react'

const Projects = () => {
  const [activeProject, setActiveProject] = useState(0)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [showVideo, setShowVideo] = useState(false)
  const videoRef = useRef(null)

  const projects = [
    {
      name: 'Nuff.Cash',
      category: 'Cloud-Native Fintech Platform',
      icon: Wallet,
      description: 'Production-grade, cloud-native fintech backend deployed on $0/month infrastructure. Features dual-service architecture with Spring Boot, distributed database, and comprehensive security stack including OFAC sanctions screening and KYC verification.',
      isMobile: true,
      images: [
        '/images/nuff-cash/image.png',
        '/images/nuff-cash/image-1.png',
        '/images/nuff-cash/image-2.png',
        '/images/nuff-cash/image-3.png',
        '/images/nuff-cash/image-4.png',
      ],
      video: null,
      tech: ['Spring Boot 2.7/3.3', 'Java 11/17', 'TiDB Cloud', 'Oracle Cloud', 'React 18', 'TypeScript', 'Firebase Auth', 'AWS Secrets Manager'],
      highlights: [
        'Dual-service architecture: 20 REST controllers, 27 services, 28 JPA repositories, 38 entities',
        'IronClad Ledger double-entry accounting with multi-tenant architecture',
        'Pluggable ledger abstraction via Strategy Pattern for zero-downtime backend swaps',
        'Production security: Firebase JWT/OAuth2, OFAC screening (7,394+ names), AES-256 encryption',
        '18 Flyway migrations, 70+ Postman endpoints in 19 functional groups',
      ],
      metrics: [
        { label: 'Monthly Transactions', value: '1M+' },
        { label: 'Uptime', value: '99.99%' },
        { label: 'Infra Cost', value: '$0/mo' },
      ],
      link: 'https://nuffcash.netlify.app/',
    },
    {
      name: 'SoloForge',
      category: 'Autonomous COO for Solopreneurs',
      icon: Bot,
      description: 'A 4-agent sequential multi-agent pipeline using CrewAI + Anthropic Claude Sonnet 4 that transforms a single lead input into a complete revenue lifecycle: lead research → personalized proposal → invoice → 6-month ML revenue forecast.',
      isMobile: false,
      images: [
        '/images/soloforge/soloforge-screenshot.png',
      ],
      video: '/images/soloforge/soloforge-demo.mp4',
      tech: ['CrewAI', 'Anthropic Claude Sonnet 4', 'Snowflake Cortex', 'Skyfire', 'Pydantic v2', 'Streamlit', 'Composio'],
      highlights: [
        'Complete lead-to-revenue lifecycle in single structured pipeline',
        'Pydantic v2 output contracts for validated context chaining between agents',
        '4-action lead routing engine (PROPOSE/NURTURE/DISQUALIFY/UPSELL) with confidence scoring',
        'Skyfire AI-native autonomous invoicing with USDC + card payments',
        'Snowflake Cortex ML 6-month revenue forecasting with confidence bounds',
        'Human-in-the-loop approval gate with automatic 10% platform fee collection',
        '7-table Snowflake schema with VARIANT columns and clustering keys',
      ],
      metrics: [
        { label: 'Target Market', value: '29.8M' },
        { label: 'Agents', value: '4' },
        { label: 'Platform Cost', value: '$0' },
      ],
    },
    {
      name: 'eMasters CRM',
      category: 'Enterprise CRM Platform',
      icon: Building2,
      description: 'Full-featured customer relationship management platform with campaign management, contact tracking, predictive analytics, and advanced dashboards serving 20K+ users.',
      isMobile: true,
      images: [
        '/images/emasters/dashboard.png',
        '/images/emasters/app-2.png',
        '/images/emasters/app-3.png',
        '/images/emasters/app-4.png',
        '/images/emasters/app-5.png',
        '/images/emasters/crm_design.png',
        '/images/emasters/emasterApp.png',
      ],
      video: null,
      tech: ['React', 'Python', 'AWS', 'Kubernetes', 'PostgreSQL', 'Docker', 'Linear Regression', 'KNN'],
      highlights: [
        'Microservices platform with predictive analytics reducing latency 35%',
        'ML-powered customer retention improvements of 25%',
        'Cloud-native AWS infrastructure with CI/CD pipelines',
        'Integrated 3rd-party systems and payment platforms',
        'Automated workflows reducing operational costs 15%',
      ],
      metrics: [
        { label: 'Users', value: '20K+' },
        { label: 'Latency Reduction', value: '35%' },
        { label: 'Retention Boost', value: '25%' },
      ],
    },
    {
      name: 'xTime Platform',
      category: 'Automotive Service Management',
      icon: Car,
      description: 'B2B/B2C SaaS platform for automotive dealerships enabling intelligent service scheduling, workflow management, and multi-point inspection with customer communication tools.',
      isMobile: false,
      images: [
        '/images/xtime/workflow.png',
        '/images/xtime/dashboard.png',
        '/images/xtime/architecture.png',
        '/images/xtime/mobile.png',
      ],
      video: null,
      tech: ['Java', 'Spring Boot', 'React', 'MySQL', 'AWS', 'Microservices', 'REST APIs'],
      highlights: [
        'Integration across 3,000+ auto dealerships',
        'Reduced system downtime by 20%',
        'Multi-stakeholder OEM partner management',
        'Real-time service scheduling and communication',
      ],
      metrics: [
        { label: 'Dealerships', value: '3,000+' },
        { label: 'Downtime Reduction', value: '20%' },
        { label: 'Reliability', value: '99.9%' },
      ],
    },
  ]

  const nextImage = () => {
    if (projects[activeProject].images.length === 0) return
    setCurrentImageIndex((prev) =>
      prev === projects[activeProject].images.length - 1 ? 0 : prev + 1
    )
  }

  const prevImage = () => {
    if (projects[activeProject].images.length === 0) return
    setCurrentImageIndex((prev) =>
      prev === 0 ? projects[activeProject].images.length - 1 : prev - 1
    )
  }

  const handleProjectChange = (index) => {
    setActiveProject(index)
    setCurrentImageIndex(0)
    setShowVideo(false)
  }

  const handlePlayVideo = () => {
    setShowVideo(true)
  }

  const handleCloseVideo = () => {
    setShowVideo(false)
    if (videoRef.current) {
      videoRef.current.pause()
    }
  }

  const IconComponent = projects[activeProject].icon
  const currentProject = projects[activeProject]
  const hasVideo = currentProject.video !== null

  return (
    <section id="projects" className="py-24 relative bg-darker/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
            Featured <span className="text-gradient">Projects</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Production systems and AI pipelines built throughout my career.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {projects.map((project, index) => (
            <button
              key={index}
              onClick={() => handleProjectChange(index)}
              className={`px-6 py-3 rounded-full font-medium transition-all flex items-center gap-2 ${activeProject === index
                ? 'bg-gradient-to-r from-primary to-secondary text-white'
                : 'glass text-gray-300 hover:text-white hover:border-primary/50'
                }`}
            >
              <project.icon size={18} />
              {project.name}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div className="relative group">
            {currentProject.images.length > 0 ? (
              <>
                <div 
                  className={`rounded-2xl overflow-hidden glass ${currentProject.isMobile ? 'aspect-[3/4] max-w-md mx-auto' : 'aspect-video'} ${hasVideo ? 'cursor-pointer' : ''}`}
                  onClick={hasVideo ? handlePlayVideo : undefined}
                >
                  <div className="w-full h-full bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center p-4 relative">
                    <img
                      src={currentProject.images[currentImageIndex]}
                      alt={`${currentProject.name} screenshot`}
                      className={`max-w-full max-h-full ${currentProject.isMobile ? 'object-contain rounded-xl shadow-2xl' : 'object-contain'}`}
                      onError={(e) => {
                        e.target.style.display = 'none'
                      }}
                    />
                    
                    {/* Play button overlay for videos */}
                    {hasVideo && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-20 h-20 rounded-full bg-primary/90 flex items-center justify-center shadow-2xl transform group-hover:scale-110 transition-transform">
                          <Play size={40} className="text-white ml-1" fill="white" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Watch Demo button for video projects */}
                {hasVideo && (
                  <div className="flex justify-center mt-4">
                    <button
                      onClick={handlePlayVideo}
                      className="flex items-center gap-2 px-6 py-2 rounded-full glass text-primary hover:bg-primary/10 transition-all"
                    >
                      <Play size={18} />
                      Watch Demo Video
                    </button>
                  </div>
                )}

                {currentProject.images.length > 1 && !hasVideo && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full glass flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white/20"
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full glass flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white/20"
                    >
                      <ChevronRight size={24} />
                    </button>

                    <div className="flex justify-center gap-2 mt-4">
                      {currentProject.images.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setCurrentImageIndex(i)}
                          className={`w-2 h-2 rounded-full transition-all ${currentImageIndex === i ? 'bg-primary w-6' : 'bg-white/30 hover:bg-white/50'
                            }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="aspect-video rounded-2xl glass flex items-center justify-center">
                <div className="text-center p-8">
                  <IconComponent className="w-24 h-24 text-primary/50 mx-auto mb-4" />
                  <p className="text-gray-400">AI-Powered Autonomous System</p>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div>
              <span className="text-primary text-sm font-medium">{currentProject.category}</span>
              <h3 className="text-3xl font-display font-bold text-white mt-1">
                {currentProject.name}
              </h3>
            </div>

            <p className="text-gray-300 leading-relaxed">
              {currentProject.description}
            </p>

            <div className="grid grid-cols-3 gap-4">
              {currentProject.metrics.map((metric, i) => (
                <div key={i} className="glass rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-gradient">{metric.value}</div>
                  <div className="text-xs text-gray-400 mt-1">{metric.label}</div>
                </div>
              ))}
            </div>

            <div>
              <h4 className="text-white font-semibold mb-3">Key Highlights</h4>
              <div className="space-y-2 max-h-40 overflow-y-auto pr-2">
                {currentProject.highlights.map((highlight, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <p className="text-gray-400 text-sm">{highlight}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {currentProject.tech.map((tech, i) => (
                <span key={i} className="px-3 py-1 rounded-full bg-white/5 text-primary text-sm border border-primary/20">
                  {tech}
                </span>
              ))}
            </div>

            {currentProject.link && (
              <a
                href={currentProject.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-primary to-secondary text-white font-semibold hover:shadow-lg hover:shadow-primary/25 transition-all"
              >
                Visit Project
                <ExternalLink size={18} />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {showVideo && currentProject.video && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={handleCloseVideo}
        >
          <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            {/* Close button */}
            <button
              onClick={handleCloseVideo}
              className="absolute -top-12 right-0 w-10 h-10 rounded-full glass flex items-center justify-center text-white hover:bg-white/20 transition-all z-10"
            >
              <X size={24} />
            </button>
            
            {/* Video player */}
            <div className="rounded-2xl overflow-hidden glass">
              <video
                ref={videoRef}
                src={currentProject.video}
                controls
                autoPlay
                className="w-full aspect-video"
              >
                Your browser does not support the video tag.
              </video>
            </div>
            
            {/* Video title */}
            <div className="text-center mt-4">
              <h4 className="text-white font-display font-semibold">{currentProject.name} Demo</h4>
              <p className="text-gray-400 text-sm">{currentProject.category}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

export default Projects
